import { faEnvelope, faLocationDot, faPhone } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Client from '@spike/client-model';
import { formatPhoneNumber } from '@spike/phone-utils';
import { FunctionComponent, ReactNode } from 'react';
import { useMarketplace } from 'hooks';
import styles from './WaitlistClientInfo.module.scss';

interface WaitlistClientInfoProps {
    client?: Client;
    /** Shown while the client profile loads. */
    fallback: { name: string; phone?: string | null; email?: string | null };
    action?: ReactNode;
    /** Makes the name a link to the client profile. */
    onNameClick?: () => void;
}

const formatAddress = (client?: Client) => {
    const address = client?.addresses?.find(current => current.main) ?? client?.addresses?.[0] ?? client?.address;
    return address ? [address.street, address.city, address.state].filter(Boolean).join(', ') : undefined;
};

export const WaitlistClientInfo: FunctionComponent<WaitlistClientInfoProps> = ({
    client,
    fallback,
    action,
    onNameClick
}) => {
    const marketplace = useMarketplace();
    const name = client ? `${client.firstName} ${client.lastName ?? ''}`.trim() : fallback.name;
    const rawPhone = client?.phone || fallback.phone;
    const phone = rawPhone ? formatPhoneNumber(rawPhone, marketplace.basics.address.country!.id) : undefined;
    const email = client?.email || fallback.email;
    const address = formatAddress(client);
    const tags = [...(client?.tags?.positive ?? []), ...(client?.tags?.negative ?? [])];

    return (
        <div className={styles.root}>
            <div className={styles.info}>
                <p className={styles.name}>
                    {onNameClick ? (
                        <button
                            type="button"
                            className={styles.nameLink}
                            onClick={onNameClick}
                        >
                            {name}
                        </button>
                    ) : (
                        name
                    )}
                </p>

                {phone && (
                    <p className={styles.line}>
                        <FontAwesomeIcon icon={faPhone} />
                        {phone}
                    </p>
                )}
                {email && (
                    <p className={styles.line}>
                        <FontAwesomeIcon icon={faEnvelope} />
                        {email}
                    </p>
                )}
                {address && (
                    <p className={styles.line}>
                        <FontAwesomeIcon icon={faLocationDot} />
                        {address}
                    </p>
                )}

                {tags.length > 0 && (
                    <div className={styles.tags}>
                        {tags.map(tag => (
                            <span
                                key={tag.id}
                                className={styles.tag}
                            >
                                {tag.name}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {action}
        </div>
    );
};

export default WaitlistClientInfo;
